import { Container, Sprite } from 'pixi.js';
import { TILE_HALF_H, TILE_HALF_W } from '@enugu/shared';
/**
 * Chunked floor tilemap. Chunks (default 16x16 tiles) are static sprite
 * containers inside one render group: Pixi caches the group's batched
 * geometry, so a static floor costs ~1 draw call and almost no CPU per
 * frame no matter how far the camera moves. Chunks outside the view are
 * hidden (one visibility flip when crossing a chunk border).
 *
 * Floor-level decals (puddles, mats, tap marker) live in `decals`, drawn
 * after the tiles and before the sorted world layer.
 */
export class FloorLayer {
    container;
    tiles;
    decals;
    width;
    height;
    chunkSize;
    chunks = [];
    cols;
    visibleChunks = 0;
    /** Extra px around chunk bounds for art that overhangs a tile (kerbs, grass tufts). */
    overhang = 24;
    constructor(width, height, texFn, chunkSize = 16) {
        this.width = width;
        this.height = height;
        this.chunkSize = chunkSize;
        this.container = new Container({ label: 'ground', isRenderGroup: true });
        this.tiles = new Container({ label: 'floor-tiles' });
        this.decals = new Container({ label: 'floor-decals' });
        this.container.addChild(this.tiles, this.decals);
        this.cols = Math.ceil(width / chunkSize);
        const rows = Math.ceil(height / chunkSize);
        // Chunks are added in back-to-front order (by chunk x+y) so overhanging
        // art of nearer chunks draws over farther ones.
        const order = [];
        for (let cy = 0; cy < rows; cy++)
            for (let cx = 0; cx < this.cols; cx++)
                order.push([cx, cy]);
        const built = new Array(order.length);
        for (const [cx, cy] of order)
            built[cy * this.cols + cx] = this.buildChunk(cx, cy, texFn);
        order.sort((a, b) => a[0] + a[1] - (b[0] + b[1]));
        for (const [cx, cy] of order)
            this.tiles.addChild(built[cy * this.cols + cx].container);
        this.chunks.push(...built);
    }
    buildChunk(cx, cy, texFn) {
        const n = this.chunkSize;
        const x0 = cx * n;
        const y0 = cy * n;
        const x1 = Math.min(this.width, x0 + n);
        const y1 = Math.min(this.height, y0 + n);
        const container = new Container({ label: `chunk ${cx},${cy}` });
        const sprites = new Array(n * n).fill(null);
        // Within a chunk, draw rows back to front (x + y ascending).
        for (let s = x0 + y0; s <= x1 - 1 + y1 - 1; s++) {
            for (let x = x0; x < x1; x++) {
                const y = s - x;
                if (y < y0 || y >= y1)
                    continue;
                const tex = texFn(x, y);
                if (!tex)
                    continue;
                const spr = new Sprite(tex);
                // Anchor = bottom-centre of the 1x1 footprint = bottom vertex of the diamond.
                spr.position.set((x - y) * TILE_HALF_W, (x + y + 2) * TILE_HALF_H);
                container.addChild(spr);
                sprites[(y - y0) * n + (x - x0)] = spr;
            }
        }
        const pad = this.overhang;
        return {
            container,
            sprites,
            left: (x0 - y1) * TILE_HALF_W - pad,
            right: (x1 - y0) * TILE_HALF_W + pad,
            top: (x0 + y0) * TILE_HALF_H - pad,
            bottom: (x1 + y1) * TILE_HALF_H + pad,
        };
    }
    /** Changes one tile's texture (building mode, decay). */
    setTile(x, y, tex) {
        const n = this.chunkSize;
        const c = this.chunks[Math.floor(y / n) * this.cols + Math.floor(x / n)];
        const spr = c?.sprites[(y % n) * n + (x % n)];
        if (spr)
            spr.texture = tex;
    }
    /** Hides chunks outside the view; returns the number of visible tiles (stat). */
    cull(view) {
        let tiles = 0;
        let visible = 0;
        for (const c of this.chunks) {
            const vis = c.right > view.left && c.left < view.right && c.bottom > view.top && c.top < view.bottom;
            if (c.container.visible !== vis)
                c.container.visible = vis;
            if (vis) {
                visible++;
                tiles += c.container.children.length;
            }
        }
        this.visibleChunks = visible;
        return tiles;
    }
}
